import { useEffect, useState } from 'react';
import { exchangeApi } from '../../services/api';
import type { Exchange } from '../../types';
import type { Language } from '../../i18n';
import { useTranslation } from '../../hooks/useTranslation';

interface ExchangeSelectionProps {
  selectedExchange: string | null;
  onSelect: (exchangeId: string) => void;
  language: Language;
}

const ExchangeSelection = ({ selectedExchange, onSelect }: ExchangeSelectionProps) => {
  const { t } = useTranslation();
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadExchanges();
  }, []);

  const loadExchanges = async () => {
    try {
      const response = await exchangeApi.getExchanges();
      setExchanges(response.exchanges || []);
    } catch (error) {
      console.error('Error loading exchanges:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderExchangeIcon = (exchange: Exchange) => {
    if (exchange.icon) {
      if (exchange.icon.startsWith('http')) {
        return (
          <img 
            src={exchange.icon} 
            alt={exchange.name}
            className="w-16 h-16 object-contain rounded-lg"
          />
        );
      } else {
        // Emoji
        return (
          <div className="w-16 h-16 bg-tg-secondary rounded-lg flex items-center justify-center text-4xl">
            {exchange.icon}
          </div>
        );
      }
    }
    // Default fallback
    return (
      <div className="w-16 h-16 bg-tg-secondary rounded-lg flex items-center justify-center">
        <span className="text-white font-bold text-lg">
          {exchange.name.charAt(0).toUpperCase()}
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div>
        <h2 className="text-xl font-bold text-white mb-2">
          {t('linkExchange.selectExchange')}
        </h2>
        <p className="text-sm text-tg-hint mb-6">
          {t('linkExchange.subtitle')}
        </p>
        <div className="grid grid-cols-2 gap-4">
          {[1, 2].map((i) => (
            <div key={i} className="bg-tg-secondary rounded-xl p-6 animate-pulse">
              <div className="w-16 h-16 bg-gray-700 rounded-lg mx-auto mb-3" />
              <div className="h-4 bg-gray-700 rounded w-20 mx-auto" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-xl font-bold text-white mb-2">
        {t('linkExchange.selectExchange')}
      </h2>
      <p className="text-sm text-tg-hint mb-6">
        {t('linkExchange.subtitle')}
      </p>
      
      {exchanges.length === 0 ? (
        <div className="text-center py-8 text-tg-hint">
          {t('exchanges.noExchangesAvailable')}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {exchanges.map((exchange) => {
            const isSelected = selectedExchange === exchange.id;
            return (
              <button
                key={exchange.id}
                onClick={() => onSelect(exchange.id)}
                className={`relative rounded-xl p-6 border-2 transition-all duration-300 ${
                  isSelected 
                    ? 'border-primary bg-primary/10 scale-105' 
                    : 'bg-tg-secondary border-transparent hover:border-gray-700 hover:scale-[1.02]'
                }`}
              >
                {isSelected && (
                  <div className="absolute top-2 right-2 w-7 h-7 bg-primary rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
                <div className="flex flex-col items-center gap-3">
                  {renderExchangeIcon(exchange)}
                  <div className={`text-base font-semibold transition-colors ${isSelected ? 'text-primary' : 'text-white'}`}>
                    {exchange.name}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ExchangeSelection;
